import type { Translation } from '../types.ts';
import type en from '../en/websites.ts';

export default {
    shared: {
        newWebsite: 'Nieuwe website',
        underConstruction: 'In aanbouw',
        noIndex: 'Geen index',
        validationFailed: 'Validatie mislukt.',
        showOutput: 'Uitvoer tonen',
        hideOutput: 'Uitvoer verbergen',
        age: {
            seconds: '{n}s geleden',
            minutes: '{n}m geleden',
            hours: '{n}u geleden',
            days: '{n}d geleden',
        },
    },
    list: {
        title: 'Websites',
        stats: {
            websites: 'Websites',
            inactiveCount: '{n} inactief',
        },
        searchPlaceholder: 'Zoeken op code of naam…',
        columns: {
            locales: 'Talen',
            storage: 'Opslag',
        },
        noIndexTitle: 'Stuurt noindex op elke pagina en blokkeert alles in robots.txt',
        underConstructionTitle: 'Bezoekers krijgen een binnenkort-online-pagina en een 503; alleen toegestane adressen zien de site',
        editing: 'Bezig met bewerken',
        switchTo: 'Overschakelen',
        deleted: 'Verwijderd',
        deletedTitle: 'Offline, definitief verwijderd op {date}',
        undoDelete: 'Verwijderen ongedaan maken',
        noMatches: 'Geen websites gevonden voor je zoekopdracht.',
        empty: 'Nog geen websites.',
    },
    edit: {
        tabs: {
            general: 'Algemeen',
            contact: 'Contact',
            theme: 'Thema',
            hosts: 'Hosts',
            forms: 'Formulieren',
            moduleSettings: 'Module-instellingen',
            modules: 'Modules',
            backups: 'Back-ups',
        },
        banners: {
            deleted: 'Verwijderd. De site is offline en wordt op {date} definitief verwijderd, met bestanden en back-ups.',
            underConstruction: 'In aanbouw. Bezoekers krijgen een “binnenkort online”-pagina en een {status}; alleen de toegestane adressen zien de site. Zet dit uit onder Algemeen → In aanbouw zodra de site live gaat.',
            noindex: 'Verborgen voor zoekmachines. Elke pagina stuurt {noindex} en robots.txt blokkeert alles. Zet dit uit onder Algemeen → Zoekmachines zodra de site live gaat.',
        },
        toasts: {
            created: 'Website aangemaakt',
            hostNotAdded: 'Website aangemaakt, maar de host is niet toegevoegd: {reason}',
            hostValidationFailed: 'validatie mislukt',
            syncQueued: 'Synchronisatie in de wachtrij. De reviews verschijnen binnen een paar minuten.',
        },
        confirmDelete: 'Website "{name}" verwijderen? Hij gaat nu offline en wordt over 30 dagen definitief verwijderd, met pagina\'s, bestellingen, media, bestanden en back-ups. Tot dan kan een superbeheerder dit ongedaan maken.',
        identity: {
            title: 'Identiteit',
            codeHintNew: 'Volgt de naam totdat je zelf iets typt. Kleine letters, cijfers en streepjes (2 tot 64).',
            codeHint: 'Kleine letters, cijfers en streepjes (2 tot 64)',
        },
        firstHost: {
            title: 'Eerste host',
            hint: 'Volgt de code als {example} totdat je zelf iets typt. Wordt toegevoegd als canonieke host; meer hosts voeg je daarna toe op het tabblad Hosts. Laat leeg om geen host toe te voegen.',
        },
        searchEngines: {
            title: 'Zoekmachines',
            hide: 'Deze site verbergen voor zoekmachines',
            hideHint: 'Voor sites die nog in aanbouw zijn. Elke pagina stuurt noindex/nofollow en robots.txt blokkeert alles. Zet het uit zodra de site live gaat.',
            newNote: 'Standaard aan voor een nieuwe site. Een site die half af wordt geïndexeerd, laat nog lang na de lancering placeholderpagina\'s in Google achter.',
            onNote: 'Pagina\'s die al geïndexeerd zijn, verwijdert de zoekmachine zelf en dat duurt even. Zet dit dus aan voordat de site bereikbaar is, niet erna.',
        },
        construction: {
            hold: 'Deze site achter een tijdelijke pagina houden',
            holdHint: 'Iedereen behalve de adressen hieronder ziet een “binnenkort online”-pagina in plaats van de site, en krijgt een 503 zodat geen zoekmachine hem opslaat. De editor blijft gewoon werken.',
            newNote: 'Standaard aan voor een nieuwe site. Een site is bereikbaar zodra de hostnamen bestaan, en dat is lang voordat hij klaar is om gelezen te worden.',
            allowedTitle: 'Adressen die de echte site zien',
            removeIp: '{ip} verwijderen',
            noneListed: 'Geen voor deze site. Onze kantoren en de ZenRise-crawler zijn centraal toegestaan voor elke site, dus deze lijst is alleen voor extra adressen.',
            addAddress: 'Adres toevoegen',
            addPlaceholder: 'bijv. 203.0.113.7 of 203.0.113.0/24',
            addHint: 'Eén adres, of een reeks in CIDR-notatie.',
            alreadyListed: '{ip} staat al op de lijst.',
            connectingFrom: 'Je maakt verbinding vanaf {ip}.',
            addIt: 'Toevoegen',
            ownListed: 'Je eigen adres {ip} staat op de lijst.',
            lockout: 'Je eigen adres staat niet op deze lijst. Tenzij een reeks hierboven het dekt of je centraal bent toegestaan, krijg je na het opslaan ook de tijdelijke pagina te zien.',
        },
        locales: {
            title: 'Talen',
            defaultLocale: 'Standaardtaal',
            locales: 'Talen',
            hint: 'Kommagescheiden, bijv. en, nl, de',
        },
        mail: {
            title: 'Afzender e-mail',
            intro: 'Standaard afzenderadres (From:) voor alle uitgaande e-mail (formulierinzendingen, klantaccounts, orderbevestigingen). Verplicht: zonder dit adres wordt er geen e-mail verstuurd. Zorg dat SPF en DKIM voor het domein zijn ingesteld, zodat onze SMTP-relay mag versturen. Afwijkende adressen per kanaal en per formulier stel je in op de tabbladen {moduleSettings} en {forms}.',
            fromEmail: 'Afzenderadres',
            fromName: 'Afzendernaam (optioneel)',
            fromNamePlaceholder: 'Standaard de naam van de website',
        },
        cookies: {
            title: 'Cookies (AVG / GDPR)',
            intro: 'Toon bezoekers de cookiemelding. Functionele cookies (sessie, inloggen, winkelwagen, CSRF) staan altijd aan en vragen nooit om toestemming.',
            show: 'Cookiemelding tonen',
            policyUrl: 'URL van het cookiebeleid',
            policyUrlPlaceholder: '/cookies of https://…',
            policyUrlHint: 'Gelinkt vanuit het onderdeel \'Lees meer\' van de melding.',
            readMore: 'Tekst bij Lees meer',
            readMoreHint: 'Platte tekst die verschijnt als de bezoeker op \'Lees meer\' klikt. Laat leeg voor de standaardtekst.',
            version: 'Versie van het beleid',
            versionNote: 'Huidig: {version}. Verhoog de versie om elke bezoeker opnieuw toestemming te vragen (bestaande toestemmingscookies vervallen).',
            bump: 'Versie verhogen',
        },
        reviews: {
            title: 'Google-reviews',
            intro: 'Haal de Google Bedrijfsprofiel-reviews van deze site op voor de publieke widget. Een dagelijkse taak houdt de reviews actueel; profielfoto\'s van reviewers worden gedownload, zodat de widget alles zelf host. Het Place id wordt bij het opslaan bij Google gecontroleerd.',
            placeId: 'Google Place id',
            placeIdHint: 'Te vinden met Google\'s Place ID Finder. Laat leeg om uit te zetten.',
            sync: 'Synchroniseren',
            syncNote: 'Draait dagelijks om 04:00 UTC. Start nu een synchronisatie om te verversen na het instellen.',
            queuing: 'In wachtrij zetten…',
            syncNow: 'Nu synchroniseren',
        },
        theme: {
            title: 'Thema',
            hintNew: 'Volgt de code totdat je zelf iets typt. Komt overeen met de mapnaam op NFS.',
            hint: 'Themacode; komt overeen met de mapnaam op NFS.',
            repository: 'Thema-repository',
            repositoryHintNew: 'Volgt de code als {example} totdat je zelf iets typt.',
            repositoryHint: 'Gitea-pad {path} (bijv. {example}), of de HTTPS-clone-URL van een repository op een andere git-host (bijv. {url}).',
            repositoryToken: 'Toegangstoken repository',
            repositoryTokenHint: 'Voor een privé-repository: het token, of gebruiker:token voor een host die er een gebruikersnaam bij wil (GitLab oauth2:<token>, Bitbucket x-token-auth:<token>). Bij een andere repository vervalt het opgeslagen token, tenzij je hier een nieuw invult.',
            removeRepositoryToken: 'Opgeslagen token verwijderen',
            superOnly: 'Alleen een superbeheerder kan het thema of de repository wijzigen.',
            activeVersion: 'Actieve versie: {version}',
        },
        hosts: {
            title: 'Hosts',
            hostname: 'Hostnaam',
            canonical: 'Canoniek',
            redirectsTo: 'Stuurt door naar',
            makeCanonical: 'Canoniek maken',
            nowhere: '(nergens)',
            samePath: 'zelfde pad',
            empty: 'Nog geen hosts.',
            addHostname: 'Hostnaam toevoegen',
            redirectOptional: 'Stuurt door naar (optioneel)',
            redirectHint: 'Een vanity-domein kan de ene pagina noemen waar het voor staat: een bezoek aan de homepage stuurt dan met een 301 daarheen door in plaats van naar onze homepage. Leeg houdt het pad dat de bezoeker opvroeg.',
            canonicalNote: 'De canonieke host is de host waar al het andere naar doorstuurt, dus hij kan zelf nergens naar doorsturen.',
            confirmRemove: 'Deze host verwijderen?',
        },
    },
    company: {
        sections: {
            contact: 'Contact en adres',
            company: 'Bedrijf',
            social: 'Social media',
            whatsapp: 'WhatsApp-knop',
        },
        lookup: {
            fillFrom: 'Invullen vanuit',
            buttons: {
                google: 'Google',
                kvk: 'KvK',
                website: 'Website',
            },
            kvkUnavailable: 'Hiervoor is een KvK API-sleutel (KVK_API_KEY) nodig op deze installatie.',
            sources: {
                google: 'Invullen vanuit Google',
                kvk: 'Invullen vanuit het KvK-register',
                website: 'Invullen vanuit de website',
            },
            query: {
                google: 'Bedrijfsnaam en plaats',
                kvk: 'KvK-nummer of bedrijfsnaam',
            },
            queryHint: {
                google: 'Zoals je het in Google Maps zou zoeken.',
                kvk: 'Zet ", plaats" achter een naam om in één plaats te zoeken, bijv. Bakkerij Jansen, Breda.',
            },
            url: 'Adres van de website',
            urlHint: 'Meestal de homepage: de footer linkt naar de social media-profielen. Er wordt één pagina gelezen.',
            read: 'Lezen',
            noResults: 'Niets gevonden. Probeer minder woorden of een andere spelling.',
            notFound: 'Dit resultaat bestaat niet meer. Zoek opnieuw.',
            failed: 'Opzoeken is mislukt: {reason}',
            nothingFound: 'Er is niets gevonden om over te nemen.',
            nothingNew: 'Alles wat gevonden is, staat er al.',
            reviewIntro: 'Vink aan wat je wilt overnemen. Velden die al zijn ingevuld, staan niet aangevinkt: wat je zelf hebt getypt, wordt alleen vervangen als je dat kiest.',
            field: 'Veld',
            found: 'Gevonden',
            now: 'Nu',
            empty: 'leeg',
            apply: 'Overnemen | 1 waarde overnemen | {n} waarden overnemen',
            applyAndScan: 'Overnemen en dan de website lezen',
            applied: 'Niets overgenomen. | 1 waarde overgenomen. Sla op om die te bewaren. | {n} waarden overgenomen. Sla op om ze te bewaren.',
            kvkKinds: {
                hoofdvestiging: 'Hoofdvestiging',
                nevenvestiging: 'Nevenvestiging',
                rechtspersoon: 'Rechtspersoon',
            },
        },
        contact: {
            title: 'Contact',
            intro: 'Gebruikt door het thema voor de header, de footer en de contactpagina, en voor de structured data van de site. Thema\'s lezen deze gegevens met {fn}. Laat je een veld leeg, dan slaat het thema het over.',
            phoneHint: 'Wordt getoond zoals getypt. De tel:-link wordt ervan afgeleid.',
            emailHint: 'Het publieke adres, niet het adres waarvandaan e-mail wordt verstuurd.',
            whatsappHint: 'Inclusief landcode: wa.me-links hebben die nodig.',
        },
        address: {
            title: 'Adres',
            street: 'Straat en huisnummer',
            postalCode: 'Postcode',
            city: 'Plaats',
            countryHint: 'Code van twee letters. Straat en plaats zijn allebei nodig voordat het adres in de structured data verschijnt.',
        },
        registration: {
            title: 'Registratie',
            kvk: 'KvK-nummer',
            vat: 'Btw-nummer',
        },
        business: {
            title: 'Bedrijf voor zoekmachines',
            intro: 'Alleen gepubliceerd als gestructureerde data, voor lokaal zoeken en de kaartresultaten. Laat het type leeg voor een organisatie zonder plek waar klanten langskomen of vanwaar u bij hen komt.',
            type: 'Soort bedrijf',
            noType: 'Geen (een organisatie)',
            typeHint: 'Het meest specifieke schema.org-type dat klopt, bijv. HomeAndConstructionBusiness voor een schoonmaak- of bouwbedrijf.',
            areaServed: 'Werkgebied',
            areaServedHint: 'Plaatsen, gescheiden door komma\'s.',
            openingHours: 'Openingstijden',
            openingHoursHint: 'Eén tijdvak per regel: dagen Mo Tu We Th Fr Sa Su, dan de tijden, bijv. Mo-Fr 08:00-17:00.',
            priceRange: 'Prijsklasse',
            foundingDate: 'Opgericht',
            latitude: 'Breedtegraad',
            longitude: 'Lengtegraad',
            coordinatesHint: 'In Google Maps: klik met rechts op de locatie en klik op de coördinaten om ze te kopiëren.',
            googleMaps: 'Google Maps-vermelding',
            googleMapsHint: 'De volledige https://-link naar het bedrijf op Google Maps.',
        },
        social: {
            title: 'Social media-profielen',
            intro: 'Volledige URL\'s. Deze worden gepubliceerd als de bekende profielen van de organisatie; een verkeerde vertelt zoekmachines dat de site van iemand anders is.',
            searchIntro: 'Staat het niet op de website? Zoek de naam via Google op:',
        },
    },
    whatsapp: {
        title: 'WhatsApp-knop',
        intro: 'De zwevende chatknop, getoond waar het thema hem plaatst ({fn}).',
        show: 'De WhatsApp-knop tonen',
        noNumber: 'Nog geen WhatsApp-nummer, dus de knop blijft verborgen. Vul het WhatsApp-veld onder Contact en adres of het nummer hieronder in.',
        number: 'Nummer',
        numberHint: 'Leeg = het WhatsApp-nummer onder Contact en adres. Inclusief landcode, bijv. +31.',
        openingMessage: 'Openingsbericht ({locale})',
        openingMessageDefault: 'Openingsbericht ({locale}, standaard)',
        openingMessageHint: 'Tekst waarmee de chat opent; de bezoeker kan die nog aanpassen.',
        label: 'Label ({locale})',
        labelHint: 'Naast het icoon. Leeg = alleen het icoon.',
        fallbackNote: 'Een taal die leeg blijft, gebruikt de tekst van de standaardtaal.',
        countries: 'Alleen tonen in deze landen',
        countriesHint: 'Leeg = elke bezoeker. Met landen in de lijst zien bezoekers van elders (of van wie het land niet te bepalen is) de knop niet.',
        removeCountry: '{country} verwijderen',
        addCountry: 'Land toevoegen…',
    },
} satisfies Translation<typeof en>;
